import {
	IconButton,
	InputAdornment,
	TextField,
	makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ProjectSearchView from "Components/Project/ProjectSearchView";
import { color } from "Configs/theme";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import React, { useEffect, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { useFieldControl } from "./FieldHelper";
import { useProjects } from "Components/Project/hooks/useProjects";

export default function ProjectField<T extends FieldValues>(props: {
	name: string;
	label: string;
	control: Control<T>;
	disabled?: boolean;
}) {
	const fieldControl = useFieldControl<string>(props.control, props.name);
	const { isNative } = useApp();
	const { value, error, setValue, register, unregister } = fieldControl;
	const projectName = useProjects(
		(state) => state.projects.find((project) => project.id === value)?.name
	);
	const { t } = useTranslation();
	const [isAutoFocusEnabled, setIsAutoFocusEnabled] = useState(false);
	const classes = useStyles({
		disabled: props.disabled,
	});

	useEffect(() => {
		register(props.name);
		return () => unregister(props.name);
	}, [props.name, register, unregister]);

	if (value) {
		return (
			<TextField
				variant="outlined"
				className={classes.selectedTextField}
				label={props.label}
				fullWidth
				disabled
				onClick={() => {
					if (!props.disabled) {
						setValue("");
						setIsAutoFocusEnabled(true);
					}
				}}
				value={projectName || t("Common_Unknown")}
				InputProps={
					props.disabled
						? undefined
						: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton>
											<CloseIcon />
										</IconButton>
									</InputAdornment>
								),
						  }
				}
			/>
		);
	} else {
		return (
			<ProjectSearchView
				onClickListItem={(project) => setValue(project.id)}
				fieldError={error}
				autoFocus={isAutoFocusEnabled && !isNative}
			/>
		);
	}
}

interface StyleProps {
	disabled?: boolean;
}

const useStyles = makeStyles({
	selectedTextField: (props: StyleProps) => ({
		"& * ": {
			cursor: props.disabled ? "default" : "pointer",
		},
		"& input": {
			color: props.disabled ? "unset" : color.text,
			WebkitTextFillColor: props.disabled ? "unset" : color.text, // required for Safari
			opacity: 1, // required for iOS
		},
	}),
});
